/**
 * The season, and the district's own Chronicle.
 *
 * A district already had two clocks: the tick, and the ten-second budget cycle
 * the treasury settles on. This adds the third and coarsest one, and it is the
 * clock the whole multiplayer design is written against
 * (`DESIGN-GOVERNORS.md` §4):
 *
 * > The world advances in **Seasons** — the coarse tick at which everything
 * > settles. […] A season equals a fixed number of district budget cycles
 * > (default: 1 season = 90 cycles of the existing 10-second cycle).
 *
 * So a season is *not* a tick count here. It is ninety budget cycles, counted by
 * the treasury that settles them. That definition is the one the design document
 * gives, and it happens to be the only one that survives the cadence being a
 * world parameter: 900 sim-seconds is 54,000 ticks at 60 Hz and 5,400 at the
 * canonical 6 Hz, while ninety cycles is ninety cycles at either rate. The
 * cycle counter is already cadence-invariant and already tested as such
 * (`tests/surface.test.ts`, "settles the same number of budget cycles at either
 * rate"), so the season inherits that property rather than restating it.
 *
 * ## What settlement does: one levy, then it reports
 *
 * `DESIGN-GOVERNORS.md` §4 specifies eight ordered settlement steps. Five of them
 * settle flows the district does not have or cannot settle alone — the two flows
 * that do exist are metered continuously and allocated by a polity that does not,
 * and freight crosses on the district's own clock. `fiscal` has §8's tithe in it
 * (ADR-021) and `construction` has §7's build progress (ADR-029). The eighth
 * writes down what the district looked like when the season closed.
 *
 * Settlement therefore does exactly two things besides reporting, and it is worth
 * stating what they cost. Until ADR-021 this class could say that settlement
 * **decides nothing** — no randomness, no money, no cell — so that a world with
 * the Chronicle deleted would run identically. That is now false by two lines: a
 * district with a tithe rate on it remits money at a season boundary, and a
 * district hosting a federal building site advances its construction at one
 * (ADR-029). Neither would happen if nobody were counting seasons.
 *
 * Everything else about the claim survives, and it is the part Law 1 (*no authored
 * deltas*) actually needs: the levy is a **share of measured tax revenue** and a
 * season of build progress is a **comparison of two counts of lorries**, so there
 * is still no quantity anywhere in a season boundary that is not a sum over things
 * the district did. Nothing is drawn and nothing is invented.
 *
 * The stages are a table so that the *order* is decided once, by the design
 * document, rather than by whoever adds the next flow. The work is injected: the
 * order lives here, and what a stage does lives in whatever object can reach a
 * treasury (`SurfaceSim`). That split is what keeps the paragraph below true.
 *
 * ## Why the surface owns this, and not the district
 *
 * Law 7: **the API cannot see ants.** The Chronicle is the public record — in
 * Phase 3 it is promoted from this array to the world's — so nothing in it may
 * ever be a fact about a colony.
 *
 * The strongest available enforcement is where the writer sits. This module is
 * handed a `SeasonReadout`: a flat bag of numbers about land, town and treasury,
 * assembled by `SurfaceSim`. It holds no reference to a district, a colony, an
 * event bus or a manager, and it therefore cannot record an ant even by
 * accident. Adding one would mean widening a named interface, in a diff, under a
 * heading that says why not. `tests/season.test.ts` pins the record's key set for
 * the same reason: not because a key set is a proof, but because it makes the
 * addition a decision somebody has to make on purpose.
 *
 * The colonies keep their own ledgers, as they always have, and §16's Reckoning
 * is where the two documents are finally laid side by side.
 *
 * See `docs/seasons.md`.
 */

import { Canonical } from '../hashState';
import { CYCLES_PER_SEASON } from '../SimClock';

/**
 * The eight steps of a season settlement, in the order `DESIGN-GOVERNORS.md` §4
 * fixes them.
 *
 * The union is derived *from* the array rather than declared beside it, so there
 * is one list and it cannot fall out of sync with itself. (Contrast
 * `TASK_TYPE_ORDER` in the colony, which orders an enum that exists for other
 * reasons and therefore needs ADR-011 D57's exhaustiveness check.)
 */
export const SETTLEMENT_STAGE_ORDER = Object.freeze([
  'physics',
  'production',
  'freight',
  'settlement',
  'fiscal',
  'migration',
  'construction',
  'chronicle',
] as const);

export type SettlementStage = (typeof SETTLEMENT_STAGE_ORDER)[number];

/** What the ground is, in cells. */
export interface SeasonLand {
  /** Cells poured, cured or painted. Sealed: this number never falls. */
  paved: number;
  /** Cells still `NATURAL`. The meadow, and the only source of new food. */
  natural: number;
  /**
   * Natural cells carrying a bush. What the colonies actually eat from.
   *
   * *Natural* is load-bearing and was not, until ADR-015. A cell's vegetation is
   * a function of its position and never changes, so a count that asked only what
   * grew there reported a meadow that could not shrink — and `bushChange` below,
   * documented as *"negative when the meadow shrank"*, was always zero. The number
   * in every Chronicle record before ADR-015 is the meadow this district was
   * *born* with.
   */
  bush: number;
  /** Fruit on those bushes right now. A level, not a total. */
  fruit: number;
}

/** What the town is, in buildings and people. */
export interface SeasonTown {
  houses: number;
  built: number;
  families: number;
  corporations: number;
  cars: number;
}

/**
 * What the treasury is and what it has done.
 *
 * `pool` and `roadQuality` are *levels* — where the district stands. The rest
 * are lifetime totals, monotone sums over settled transactions, so a season's
 * flows are the difference between two consecutive records (`seasonFlows`).
 * Nothing here is stored as a per-season figure, which is what keeps the
 * Chronicle from needing a running mark of its own.
 */
export interface SeasonTreasury {
  pool: number;
  roadQuality: number;
  taxCollected: number;
  /**
   * Rent the federal estate has paid this district, lifetime.
   *
   * The only revenue line here the district's own economy did not produce. §8
   * puts leases in the district budget, so it sits beside the tax rather than in
   * the estate's own block — a treasury should be able to account for its money
   * without asking the tenant.
   *
   * **Omitted while it is zero**, unlike every other field in this group. Same
   * move `PowerDemand` already makes with its facility term, and for the same
   * reason: a treasury that has never received rent and one that could not are
   * the same treasury, and writing the key anyway would have moved the hash of
   * every Chronicle recorded before there was a landlord.
   */
  leaseCollected?: number;
  ubiDistributed: number;
  dividendsDistributed: number;
  businessGrantsPaid: number;
  infraSpending: number;
  /**
   * Dollars committed to works that yield nothing, lifetime.
   *
   * The other spending line the district decides a click at a time, beside
   * `infraSpending`, which is standing policy at a rate from the config. Kept
   * separate because the two are not the same kind of number: infrastructure buys
   * road quality, and road quality is measurable in car speed and in a household's
   * score. This buys nothing this record can point at.
   *
   * **Omitted while it is zero**, like `leaseCollected` above and for the same
   * reason: a district whose mayor has never bought anything that does not pay
   * should not have the line in its Chronicle.
   */
  worksSpending?: number;
  /**
   * Dollars paid for the power and water actually delivered, lifetime.
   *
   * §8's first new subtraction and the only one that scales with the town: every
   * home, shop, streetlight and construction site adds to it. Here rather than in
   * `SeasonFiscal` because §8 puts the bills in *the district budget*, and because
   * a reader comparing it against `taxCollected` in the same block is asking the
   * question the line exists for — what share of a season's revenue the district
   * spent on being lit.
   *
   * **Omitted while it is zero**, like the two lines above it. A meadow with no
   * buildings on it draws nothing and is billed nothing.
   */
  utilityBilled?: number;
  interestPaid: number;
}

/**
 * What the district drew, and what it did not get.
 *
 * The first of §5's five flows to reach the record. Two levels and five
 * lifetime totals, the same split as the treasury and for the same reason: a
 * season's energy is a subtraction over two consecutive lines.
 *
 * `deliveredKwCycles` is the quantity a power *bill* is a multiplication over
 * (§8, "metered at polity prices"), which is why it is recorded now, a phase
 * before anything charges for it. Law 5 — *the simulation is the court* — needs
 * the measurement to predate the contract.
 */
export interface SeasonPower {
  /** What the district was allowed to draw when the season closed. A level. */
  supplyKw: number;
  /** What it wanted at the last metered cycle. A level. */
  demandKw: number;
  /** What was switched off at that cycle. A level; zero in a district that fits. */
  shedKw: number;
  /** Buildings dark or stalled at that cycle. A level. */
  darkBuildings: number;
  meteredCycles: number;
  /** Cycles in which anything at all was shed. */
  shortfallCycles: number;
  /** Kilowatt-cycles actually delivered. The billable quantity. */
  deliveredKwCycles: number;
  /** Kilowatt-cycles asked for and refused. */
  shedKwCycles: number;
  /** Building-cycles spent dark. §10's migration will be a sum over this. */
  darkBuildingCycles: number;
}

/**
 * What the district drank, what it did not get, and what the weather was.
 *
 * The second flow in the record, and the same split as the first: levels for
 * where the district stood when the season closed, monotone sums for what it
 * drew. `deliveredM3Cycles` is the quantity a water *bill* multiplies (§8), and
 * it is here a phase before anything charges for it, for Law 5's reason.
 *
 * `rainfall` is the odd one out and the reason it is in: it is neither a level
 * the district set nor a total it accumulated, but a fact about the world the
 * season happened in. A Chronicle that recorded a district drinking half what it
 * drank last year, and did not record that it did not rain, would be a record
 * with the cause left out.
 */
export interface SeasonWater {
  /** This season's rainfall, as a fraction of a normal year. 1 in a normal one. */
  rainfall: number;
  /** What the district was allowed to draw when the season closed. A level. */
  supplyM3: number;
  /** What it wanted at the last metered cycle. A level. */
  demandM3: number;
  /** What was refused at that cycle. A level; zero in a district that fits. */
  cutM3: number;
  /** Buildings whose landscaping went unwatered at that cycle. A level. */
  parchedGardens: number;
  /** Buildings whose own supply was cut at that cycle. A level. */
  dryBuildings: number;
  meteredCycles: number;
  /** Cycles in which anything at all was cut. */
  shortfallCycles: number;
  /** Cubic metres actually delivered. The billable quantity. */
  deliveredM3Cycles: number;
  /** Cubic metres asked for and refused. */
  cutM3Cycles: number;
  /** Building-cycles of brown lawn. A picture, counted. */
  parchedGardenCycles: number;
  /** Building-cycles spent dry. §10's migration will be a sum over this too. */
  dryBuildingCycles: number;
}

/**
 * What the polity has built on this district's map, and how it ran.
 *
 * The first block in the record that is not about the district at all. A
 * facility is federal: the mayor did not build it, cannot switch it off, and — in
 * this build — was not asked about it. What the Chronicle records is therefore
 * what a district can honestly say about somebody else's machine standing on its
 * ground: how much land it took, what it drew, and how much of what it asked for
 * it got.
 *
 * There is no production figure, because there is no production
 * (`FacilityManager`). `uptimeCycles` is what a §7 contract's *"min uptime"* term
 * would be compared against, and it is recorded a phase before anything compares
 * it, for the reason Law 5 gives: the simulation is the court, and a court needs
 * the measurement to predate the case.
 *
 * The block is **omitted entirely** from a record written by a district with no
 * facility on it. An empty estate and no estate are the same thing to say nothing
 * about, and it is why ADR-015 moved no golden master.
 */
export interface SeasonFacilities {
  /** Facilities standing on the map when the season closed. A level. */
  count: number;
  /**
   * How many of them had opened. A level, and never above `count`.
   *
   * §7's two phases as one subtraction: `count - operating` is how many federal
   * building sites the district is hosting, which is a different thing to have on
   * your map than a machine hall — it takes the same land, draws nothing, employs
   * nobody, produces nothing, and brings lorries.
   */
  operating: number;
  /** Cells under a slab. Never falls; sealed land does not come back. */
  sealedCells: number;
  /** What the estate asked of the grid at the last metered cycle. A level. */
  demandKw: number;
  /** What it asked of the mains at the last metered cycle. A level. */
  demandM3: number;
  /** `min(power, water)` at that cycle, in [0, 1]. A level. */
  uptime: number;
  meteredCycles: number;
  /** Cycles in which the estate did not get everything it asked for. */
  curtailedCycles: number;
  /** Cycle-fractions actually supplied. What a contract would read. */
  uptimeCycles: number;
  /** Dollars the estate has paid the host district, lifetime. §7's statutory rate. */
  leasePaid: number;
  /** Compute-units produced, lifetime. `Σ uptime × capacity`, §4's rule. */
  computeUnits: number;
  /** Dollars spent running the estate, lifetime. Scaled by the same uptime. */
  operatingCost: number;
  /**
   * Goods-units delivered to the district's building sites, lifetime.
   *
   * Also the number of lorries that arrived, because §5 says one unit of goods is
   * one truck. What they cost is the world's block, not this one: a district
   * counts what crossed it and never what anybody paid.
   */
  goodsDelivered: number;
  /** Convoys that became a truck on these roads, lifetime. */
  convoysDispatched: number;
  /** Convoys that gave up before the gate, lifetime. */
  convoysLost: number;
  /** Convoys with no way into the district, or no room at it, lifetime. */
  convoysLapsed: number;
}

/**
 * What the world outside paid, and what it charged.
 *
 * §5's World Market, in the three prices this build has: what a compute-unit
 * fetches, what a goods-unit costs, and what a dollar of debt costs a district per
 * budget cycle.
 *
 * The block holds the **prices** and the **sums**, and the prices being here are
 * the point. §16 publishes this record as the world's archive, and a season in which
 * a governor repriced compute is a season whose figures cannot be understood
 * without the number they were computed against. `postings` says how many times
 * somebody moved one — the audit line for §5's *"prices that punish desperation"*,
 * because a market that only ever moves against a district in trouble is a fact
 * about the record rather than about any one season in it.
 *
 * `salesRevenue` is **not the district's money**, and neither is the profit a
 * reader gets by subtracting `operatingCost` from it — which is why the profit is
 * not a field. A record is a set of sums, and a stored difference between two of
 * them is a third number that can disagree with the two. Both are the polity's,
 * recorded here because the polity treasury that claims them does not exist and
 * Law 5 wants the measurement first. What the district gets out of
 * the estate is three other lines, in three other blocks: `leasePaid` above,
 * `wagesPaid` in `SeasonLabour`, and `procurementPaid` here — which arrives as
 * ordinary corporate revenue and is therefore already inside `taxCollected` by
 * the time anybody reads it.
 *
 * Omitted entirely from a record written by a district nobody has quoted a price
 * and nothing has traded in — which is every district in this repository's golden
 * masters.
 */
export interface SeasonMarket {
  /** Dollars a compute-unit fetched when the season closed. A level. */
  computePrice: number;
  /** Dollars a goods-unit cost at that moment. A level. What a convoy carried. */
  goodsPrice: number;
  /** What a dollar of debt cost per budget cycle at that moment. A level. */
  bondYield: number;
  /** Compute-units sold, lifetime. */
  computeSold: number;
  /** What they fetched, lifetime. The polity's revenue, not the district's. */
  salesRevenue: number;
  /** What running the estate cost, lifetime. The polity's spending. */
  operatingCost: number;
  /** Dollars placed with host-district corporations, lifetime. §6's fourth line. */
  procurementPaid: number;
  /**
   * Goods-units the polity bought to build here, lifetime.
   *
   * Bought when the lorry set off, so this exceeds `facilities.goodsDelivered` by
   * exactly the loads that never arrived. The gap is the price of the district's
   * roads, charged to somebody who does not pay for them.
   */
  goodsBought: number;
  /** What they cost, lifetime. §8's *"Out: facility construction"*. */
  capitalSpend: number;
  /** Budget cycles in which anything was produced or spent. */
  tradedCycles: number;
  /** Times a posted price moved. Zero in a district nobody has quoted. */
  postings: number;
}

/**
 * Who works for the polity, and what that was worth.
 *
 * The second federal block, and the first one that is about people rather than
 * property. §4's production rule takes a `min` over three terms and this is the
 * third; §6 calls it *"the mayors' quiet, permanent leverage"*, and the record is
 * where the quiet part stops — a season in which a district's staff stayed home
 * is a season the Chronicle says they stayed home.
 *
 * `wagesPaid` is federal money arriving in a district from outside it, and it is
 * recorded a phase before anything is billed for it, for Law 5's reason: §8's
 * fiscal federalism settles a tithe against measured flows, and a court needs the
 * measurement to predate the case.
 *
 * Omitted entirely from a record written by a district where nobody has ever held
 * a federal job — which is every district in this repository's golden masters.
 */
export interface SeasonLabour {
  /** Posts the estate advertised when the season closed. A level. */
  posts: number;
  /** Posts a family held. A level; below `posts` when nobody lives near enough. */
  hired: number;
  /** Posts actually worked. A level; zero at a facility whose staff are out. */
  staffed: number;
  /** Facilities under a withdrawal when the season closed. A level. */
  strikes: number;
  meteredCycles: number;
  /** Post-cycles worked. Against `posts × meteredCycles`, the season's staffing. */
  staffedCycles: number;
  /** Post-cycles nobody worked, for either reason. */
  vacancyCycles: number;
  /**
   * Family-cycles somebody was actually standing in a workplace.
   *
   * Below `staffedCycles` by however much of the season the staff spent
   * commuting, and **zero** in a district whose roads never reached the gate.
   * The wage is paid against this and not against the posts held.
   */
  shiftCycles: number;
  /** Facility-cycles spent under a withdrawal. The strike, in the record. */
  withdrawnCycles: number;
  /** Wages paid into this district's families, lifetime. */
  wagesPaid: number;
  /** Family-cycles worked. `wagesPaid` over this is the rate that was paid. */
  wageCycles: number;
}

/**
 * The households, and the one poll a governor cannot spin.
 *
 * §10 ends on the claim that makes this block the most consequential four
 * numbers in a season record:
 *
 * > Population is thus the one poll that can't be spun — a governor's true
 * > approval rating is their net migration, and it is public in the Chronicle.
 *
 * So it is published, by cause, and nothing here computes a verdict on it. A
 * reader who wants to know whether a district was governed well subtracts two
 * records and reads why people left; the record itself has no opinion, in the
 * same register `SeasonPower` uses about a brownout.
 *
 * Absent from a district where every household has always been solvent, lit and
 * watered. See `SeasonFacilities` for why absence rather than zeroes.
 */
export interface SeasonWelfare {
  /** Households below the line when the season closed. A level. */
  strained: number;
  /** Mean quality of life over the district's households, 0–1. A level. */
  qol: number;
  /** Household-cycles spent below the line. A sum. */
  strainedCycles: number;
  /** Household-cycles a family spent in a home the grid had shed. A sum. */
  darkHouseholdCycles: number;
  /** Household-cycles a family spent with the mains shut off. A sum. */
  dryHouseholdCycles: number;
  /** Families that have left the district, lifetime. A sum. */
  departures: number;
  /**
   * Cycles a vacant home stood empty because nowhere habitable was on offer.
   *
   * The half of migration that is not a departure. A district whose population
   * held steady because nobody left and one whose population held steady because
   * nobody *came* are the same figure in `town.families` and different worlds.
   */
  arrivalsRefused: number;
  /**
   * Departures by the term that took most off the score, lifetime.
   *
   * Causes that have never named a departure are absent rather than zero, for
   * the reason the block itself is: a district that has never lost anybody to a
   * brownout should not have the word in its Chronicle.
   *
   * Keyed by `DEPARTURE_CAUSE_ORDER`, and typed as a bag of strings rather than
   * by importing that union. The settler's import list is two entries long and
   * `tests/purity.test.ts` pins it: a type-only import cannot reach anything at
   * runtime, but the list is what makes widening this file a decision somebody
   * takes deliberately, and it is not worth spending for a narrower key type on
   * a record the settler only copies.
   */
  departuresBy: Readonly<Record<string, number>>;
}

/**
 * What the district bought that pays nothing.
 *
 * §10's three mercy builds, and the only block in this record whose subject is a
 * choice rather than a consequence. Everything else here is something that happened
 * to a district — the weather, an allocation, a slab, a household's arithmetic. This
 * is what somebody decided to do about it.
 *
 * It is published, and published is the point. §10 makes a covenant *"irreversible
 * except by condemnation (Charter-gated), which is the most public aggression a
 * governor can commit"*, and an aggression nobody can see is not one: a governor has
 * to be able to read that fourteen cells of this district are spoken for before
 * choosing whether to seize them anyway. So the block is legible from above, on
 * purpose, and it is the mayor's only way of saying anything at all to the polity
 * that is not a strike.
 *
 * What it does not carry, and could not: whether any of it worked. There is no count
 * here of what crossed a road safely or what ate from a hedgerow, because the
 * settler cannot see a colony (Law 7) and neither can the manager that owns these
 * numbers (`MercyManager`). A mayor's mercy is measured in what it cost them.
 *
 * Absent from a district whose mayor has never built one. See `SeasonFacilities` for
 * why absence rather than zeroes.
 */
export interface SeasonMercy {
  /** Cells deeded wild when the season closed. A level; falls by condemnation. */
  covenantCells: number;
  /** Bushes paid for and not yet standing. A level. */
  saplingCells: number;
  /** Cells with a way under the road when the season closed. A level. */
  crossingCells: number;
  /** Cells ever deeded, lifetime. Against `covenantCells`, what was taken. */
  covenantsRegistered: number;
  /** Bushes ever planted, lifetime. */
  hedgerowsPlanted: number;
  /** Planted bushes that came in. */
  hedgerowsGrown: number;
  /** Saplings whose ground was developed before they came in. */
  hedgerowsLost: number;
  /** Ways under a road ever built, lifetime. */
  crossingsBuilt: number;
  /**
   * Cells of the district's own works that a federal slab took, lifetime.
   *
   * The measured half of §10's condemnation. No Charter gates it and no
   * compensation answers it in this build, so this figure is the whole of the
   * record — which makes it the sharpest number in the Chronicle, and the only one
   * that is about a promise being broken.
   */
  condemnedCells: number;
  /** Dollars committed to all of it, lifetime. Nothing was ever returned. */
  spent: number;
}

/**
 * The district's account with the polity above it.
 *
 * §8's fiscal federalism, from the district's side. Four of the seven lines §8
 * adds to the district budget are here — the bills, the tithe, the grant, and what
 * the rationing rule refused to fund — and three are not: tolls need freight, war
 * repair needs a war, and the wages and rent already have blocks of their own
 * (`SeasonLabour`, `SeasonFacilities`).
 *
 * `titheRate` is the only *policy* figure in this whole record: everywhere else
 * the Chronicle reports quantities, and this one reports a decision somebody above
 * the district took about it. §8 calls it *"the extraction dial"* and §16 publishes
 * it, which is the point — a district being taxed at 22% is a fact its neighbours
 * and its successors are entitled to, and a governor who wants a season's silence
 * has to set the dial to zero rather than hope nobody looks.
 *
 * `infraUnpaid` is the sharpest number in the block and the one that connects it
 * to everything else in this file. It is the road maintenance the district wanted
 * and could not afford, which is §8's *"roads decay, commerce slows — the existing
 * death-spiral"* as a running total: against `treasury.roadQuality` it says why the
 * roads are what they are, and against `mercy.spent` in the same record it says
 * what a covenant actually cost.
 *
 * Absent from a district nobody has billed, tithed or rationed — an untouched
 * meadow draws nothing, so it owes nothing. See `SeasonFacilities` for why absence
 * rather than zeroes.
 */
export interface SeasonFiscal {
  /** The dial when the season closed. A level; negative is a grant coming down. */
  titheRate: number;
  /** Dollars committed outside the district and not yet covered. A level. */
  owed: number;
  /** Dollars billed for delivered power, lifetime. */
  powerBilled: number;
  /** Dollars billed for delivered water, lifetime. */
  waterBilled: number;
  /** Dollars remitted upward, lifetime. */
  tithePaid: number;
  /** Dollars received from above, lifetime. Counted apart from the tithe, not netted. */
  grantsReceived: number;
  /** Dollars of the district's own works charged against receipts, lifetime. */
  worksCommitted: number;
  /** Road maintenance the district wanted and could not fund, lifetime. */
  infraUnpaid: number;
  /** Cycles with a bill on them. Against `cycles`, how much of a season drew. */
  billedCycles: number;
  /** Cycles in which the road budget was short. A sum; the austerity, counted. */
  constrainedCycles: number;
  /** Seasons a tithe has been levied, whatever the rate. */
  levies: number;
}

/**
 * The corridor, and what crossing it came to.
 *
 * §5's freight and interchanges, from the district's side. Every figure is a count
 * of trucks or of what trucks did — there is no goods stock, no fuel and no world
 * price here, because those belong to facilities this build does not have. What it
 * has is the physical half: structures the mayor paid for, crossings that happened,
 * and the two things a crossing is worth to a district in opposite directions.
 *
 * `legsCompleted` against `legsDispatched` is §5's *"freight capacity of a polity
 * is a **measured** quantity: routes × road quality × congestion"*. Nothing in this
 * build computes that ratio; it is what the trucks did.
 *
 * `tollsEarned` against `roadWear` is the whole bargain, and the Chronicle
 * deliberately reports them in different units — dollars against points of road
 * quality — because they are not commensurable and a record that netted them would
 * be taking a position. §16 publishes the rate; whether it was worth it is the
 * reader's.
 *
 * Absent from a district that has never built an interchange and never been offered
 * a leg. See `SeasonFacilities` for why absence rather than zeroes.
 */
export interface SeasonFreight {
  /** Interchanges standing when the season closed. A level, and it only rises. */
  interchanges: number;
  /** Truck-legs offered per budget cycle when the season closed. A level. */
  offered: number;
  /** Dollars spent building interchanges, lifetime. */
  spent: number;
  /** Whole truck-legs the polity has offered, lifetime. */
  legsOffered: number;
  /** Legs that became a truck on the map, lifetime. */
  legsDispatched: number;
  /** Trucks that reached the far interchange, lifetime. What a toll is paid on. */
  legsCompleted: number;
  /** Trucks that entered and gave up, lifetime. The roads, measured. */
  legsAbandoned: number;
  /** Legs that never became a truck: no corridor, or no room at the gate. */
  legsLapsed: number;
  /** Dollars of toll, lifetime. */
  tollsEarned: number;
  /** Points of road quality transit has worn away, lifetime. */
  roadWear: number;
}

/**
 * Everything settlement is allowed to know.
 *
 * Assembled by `SurfaceSim` from readings it already takes. See the header on
 * Law 7 for why this is an interface of numbers rather than a district.
 */
export interface SeasonReadout {
  tick: number;
  simTimeMs: number;
  land: SeasonLand;
  town: SeasonTown;
  treasury: SeasonTreasury;
  power: SeasonPower;
  water: SeasonWater;
  /** Omitted by a district with no facility on it. */
  facilities?: SeasonFacilities;
  /** §5's two posted prices and what traded at them. Omitted while nothing has. */
  market?: SeasonMarket;
  /** Omitted by a district where nobody has ever held a federal job. */
  labour?: SeasonLabour;
  /** Omitted by a district where nothing has ever gone wrong for a household. */
  welfare?: SeasonWelfare;
  /** Omitted by a district whose mayor has never built something that pays nothing. */
  mercy?: SeasonMercy;
  /** Omitted by a district nobody has billed, tithed or rationed. */
  fiscal?: SeasonFiscal;
  /** Omitted by a district with no interchange and no freight offered to it. */
  freight?: SeasonFreight;
}

/** One line of the Chronicle: the district as the season closed on it. */
export interface SeasonRecord extends SeasonReadout {
  /** 1 for the first season a district lives through. */
  season: number;
  /**
   * Budget cycles settled since the world began.
   *
   * Taken from the treasury that counts them rather than from the readout, so
   * there is no second copy of the number the season is *made of*. While a tick
   * can settle at most one cycle — which is true of every cadence this world
   * runs — it is exactly `season × CYCLES_PER_SEASON`, and `tests/season.test.ts`
   * says so. It is recorded rather than derived because if that ever stopped
   * being true, a record saying which cycle it actually closed on is worth more
   * than one restating its own season number.
   */
  cycles: number;
}

/** The Chronicle, for snapshots. Append-only, and never compacted in v1. */
export interface SeasonSnapshot {
  chronicle: SeasonRecord[];
}

/** The season a district is living through, given the cycles it has settled. */
export function seasonOf(cycles: number): number {
  return Math.floor(cycles / CYCLES_PER_SEASON) + 1;
}

/**
 * A season's flows: what changed between two records.
 *
 * A reader's function, not state. The Chronicle stores levels and lifetime
 * totals; every per-season figure anyone wants is a subtraction over two
 * consecutive lines, which is why no running mark has to be snapshotted and why
 * a re-derived figure can never disagree with the record it came from.
 *
 * `previous` is omitted for the first season, where the world's own starting
 * values stand in: the totals all begin at zero, and the two levels that do not
 * (`pool`, `roadQuality`) are reported as levels rather than as flows.
 */
export function seasonFlows(record: SeasonRecord, previous?: SeasonRecord) {
  const before = previous?.treasury;
  const land = previous?.land;
  const town = previous?.town;
  const power = previous?.power;
  const water = previous?.water;
  const estate = previous?.facilities;
  const market = previous?.market;
  const labour = previous?.labour;
  const welfare = previous?.welfare;
  const mercy = previous?.mercy;
  const fiscal = previous?.fiscal;
  const freight = previous?.freight;
  return {
    season: record.season,
    cycles: record.cycles - (previous?.cycles ?? 0),
    taxCollected: record.treasury.taxCollected - (before?.taxCollected ?? 0),
    /** Rent the estate paid this season. The one thing a district is given. */
    leaseCollected: (record.treasury.leaseCollected ?? 0) - (before?.leaseCollected ?? 0),
    ubiDistributed: record.treasury.ubiDistributed - (before?.ubiDistributed ?? 0),
    dividendsDistributed:
      record.treasury.dividendsDistributed - (before?.dividendsDistributed ?? 0),
    businessGrantsPaid:
      record.treasury.businessGrantsPaid - (before?.businessGrantsPaid ?? 0),
    infraSpending: record.treasury.infraSpending - (before?.infraSpending ?? 0),
    interestPaid: record.treasury.interestPaid - (before?.interestPaid ?? 0),
    /** Positive: the treasury gained. Negative: it borrowed. */
    poolChange: record.treasury.pool - (before?.pool ?? 0),
    /** Cells sealed this season. Never negative: pavement does not come up. */
    cellsPaved: record.land.paved - (land?.paved ?? 0),
    /**
     * Negative when the meadow shrank, which is the usual direction.
     *
     * **Not a flow in a district's first season.** With no `previous` this
     * subtracts against zero, which is right for every lifetime total in this
     * function and for the three levels that genuinely open at zero — pavement,
     * families, buildings — and wrong for this one, because a meadow opens full.
     * Season 1's figure is the standing bush count wearing a plus sign, and it
     * cannot be anything else: the Chronicle's first line is the earliest
     * measurement there is. A reader who reports a change has to say nothing for
     * the first season, which is what the Season Report card does.
     */
    bushChange: record.land.bush - (land?.bush ?? 0),
    familyChange: record.town.families - (town?.families ?? 0),
    buildingChange: record.town.built - (town?.built ?? 0),
    /** Kilowatt-cycles the district actually got. What a bill would multiply. */
    deliveredKwCycles: record.power.deliveredKwCycles - (power?.deliveredKwCycles ?? 0),
    /** Kilowatt-cycles asked for and refused. Zero in a district that fits. */
    shedKwCycles: record.power.shedKwCycles - (power?.shedKwCycles ?? 0),
    /** Building-cycles spent dark or stalled. The brownout ledger, per season. */
    darkBuildingCycles: record.power.darkBuildingCycles - (power?.darkBuildingCycles ?? 0),
    /**
     * Budget cycles in which anything was shed. At most `cycles`.
     *
     * Named for its flow since there are two of them. It was `shortfallCycles`
     * while power was the only flow that could be short of anything.
     */
    powerShortfallCycles: record.power.shortfallCycles - (power?.shortfallCycles ?? 0),
    /** Cubic metres the district actually got. What a water bill would multiply. */
    deliveredM3Cycles: record.water.deliveredM3Cycles - (water?.deliveredM3Cycles ?? 0),
    /** Cubic metres asked for and refused. Zero in a district that fits. */
    cutM3Cycles: record.water.cutM3Cycles - (water?.cutM3Cycles ?? 0),
    /** Building-cycles of brown lawn. The visible half of a drought. */
    parchedGardenCycles: record.water.parchedGardenCycles - (water?.parchedGardenCycles ?? 0),
    /** Building-cycles spent with the supply cut. The measured half. */
    dryBuildingCycles: record.water.dryBuildingCycles - (water?.dryBuildingCycles ?? 0),
    /** Budget cycles in which anything was cut. At most `cycles`. */
    waterShortfallCycles: record.water.shortfallCycles - (water?.shortfallCycles ?? 0),
    /** Cells sealed under a federal slab this season. Never negative. */
    cellsSealed: (record.facilities?.sealedCells ?? 0) - (estate?.sealedCells ?? 0),
    /** Facilities sited this season. Negative is not possible: nothing is removed. */
    facilityChange: (record.facilities?.count ?? 0) - (estate?.count ?? 0),
    /**
     * Facilities that **opened** this season. §7's `CONSTRUCTION` → `OPERATION`.
     *
     * A separate line from `facilityChange` because they are separate events and
     * are usually seasons apart: one is the day a district lost twenty-four cells,
     * the other the day it gained a load, eight jobs and a machine hall.
     */
    facilitiesOpened: (record.facilities?.operating ?? 0) - (estate?.operating ?? 0),
    /** Federal building sites standing when the season closed. A level. */
    facilitiesBuilding:
      (record.facilities?.count ?? 0) - (record.facilities?.operating ?? 0),
    /** Goods-units, and therefore lorries, that reached a building site here. */
    goodsDelivered:
      (record.facilities?.goodsDelivered ?? 0) - (estate?.goodsDelivered ?? 0),
    /** Construction lorries that entered the district this season. */
    convoysDispatched:
      (record.facilities?.convoysDispatched ?? 0) - (estate?.convoysDispatched ?? 0),
    /** Construction lorries that gave up in this district's traffic. */
    convoysLost: (record.facilities?.convoysLost ?? 0) - (estate?.convoysLost ?? 0),
    /** Convoys with no way in, or no room at it. The district could not take them. */
    convoysLapsed:
      (record.facilities?.convoysLapsed ?? 0) - (estate?.convoysLapsed ?? 0),
    /**
     * Cycle-fractions the estate was actually supplied.
     *
     * Against `facilityMeteredCycles` this is the season's uptime as a ratio, and
     * it is the pair of numbers a contract term compares. Zero for a district with
     * no facility on it, which is the same answer as a facility that never ran and
     * is the honest one: nothing was supplied either way.
     */
    facilityUptimeCycles:
      (record.facilities?.uptimeCycles ?? 0) - (estate?.uptimeCycles ?? 0),
    facilityMeteredCycles:
      (record.facilities?.meteredCycles ?? 0) - (estate?.meteredCycles ?? 0),
    /** Cycles the estate was short of power or water. At most `cycles`. */
    facilityCurtailedCycles:
      (record.facilities?.curtailedCycles ?? 0) - (estate?.curtailedCycles ?? 0),
    /** Compute-units the estate produced this season. §4's rule, summed. */
    computeProduced: (record.facilities?.computeUnits ?? 0) - (estate?.computeUnits ?? 0),
    /**
     * What the estate's output fetched on the world market this season.
     *
     * The polity's revenue, in the district's Chronicle, because it was made
     * here. §16 publishes both columns and this is the one a governor is reading.
     */
    computeSales: (record.market?.salesRevenue ?? 0) - (market?.salesRevenue ?? 0),
    /** What running the estate cost the polity this season. */
    operatingCost: (record.market?.operatingCost ?? 0) - (market?.operatingCost ?? 0),
    /**
     * Sales less running costs. §8's *"facility operating profits"*, per season.
     *
     * Derived here rather than stored, so it cannot disagree with the two figures
     * it is a difference of. Negative when a curtailed facility sold less than it
     * cost to keep alive, and not clamped: a polity subsidising a machine hall it
     * cannot power is a fact §7's contracts should be able to argue about.
     */
    operatingProfit:
      ((record.market?.salesRevenue ?? 0) - (market?.salesRevenue ?? 0)) -
      ((record.market?.operatingCost ?? 0) - (market?.operatingCost ?? 0)),
    /**
     * Federal money that entered the district's *businesses* this season.
     *
     * §6's *"local procurement"*, and the only line in the split that lands in the
     * tax base. It is already inside `taxCollected` by the time anybody reads it —
     * it arrived as corporate revenue and was taxed like any other — so a reader
     * adding the two would be counting a share of it twice.
     */
    procurementPaid:
      (record.market?.procurementPaid ?? 0) - (market?.procurementPaid ?? 0),
    /**
     * What the polity spent building here this season. §8's construction line.
     *
     * Capex, not opex, and deliberately outside `operatingProfit`: a machine hall
     * that cost $72,000 to build and makes $72,000 a season is not a business that
     * broke even, it is a business that paid for itself once. A reader that wants
     * the polity's whole position in this district subtracts this from that, which
     * is what `ResidentGovernor` does with its appetite.
     */
    capitalSpend: (record.market?.capitalSpend ?? 0) - (market?.capitalSpend ?? 0),
    /** Goods-units the polity bought for this district's building sites. */
    goodsBought: (record.market?.goodsBought ?? 0) - (market?.goodsBought ?? 0),
    /** Times a world price moved this season. Zero in a district nobody quoted. */
    postings: (record.market?.postings ?? 0) - (market?.postings ?? 0),
    /**
     * Post-cycles worked this season.
     *
     * Against `labourMeteredCycles × posts` this is the season's staffing as a
     * ratio, which is the third term of §4's rule averaged over the season rather
     * than sampled at its end.
     */
    staffedCycles: (record.labour?.staffedCycles ?? 0) - (labour?.staffedCycles ?? 0),
    /** Post-cycles nobody worked: unfilled or withheld, and this does not say which. */
    vacancyCycles: (record.labour?.vacancyCycles ?? 0) - (labour?.vacancyCycles ?? 0),
    /** Facility-cycles under a withdrawal. The half of the above that was a choice. */
    withdrawnCycles: (record.labour?.withdrawnCycles ?? 0) - (labour?.withdrawnCycles ?? 0),
    labourMeteredCycles: (record.labour?.meteredCycles ?? 0) - (labour?.meteredCycles ?? 0),
    /** Wages that entered the district's families this season. */
    wagesPaid: (record.labour?.wagesPaid ?? 0) - (labour?.wagesPaid ?? 0),
    /**
     * Family-cycles actually worked this season.
     *
     * Against `staffedCycles` this is what fraction of the posted time the staff
     * were physically there. The two are equal only in a district where nobody
     * ever has to drive anywhere, and they are the same number for a different
     * reason — zero — in one whose roads do not reach the gate.
     */
    shiftCycles: (record.labour?.shiftCycles ?? 0) - (labour?.shiftCycles ?? 0),
    /**
     * Families that left this season, for any reason.
     *
     * The other half of `familyChange`, and the half that is not spinnable:
     * `familyChange` nets departures against the newcomers who filled the vacant
     * homes, so a district that lost eight families and gained eight reads as
     * flat. This one does not net.
     */
    departures: (record.welfare?.departures ?? 0) - (welfare?.departures ?? 0),
    /** Household-cycles below the line. The pressure, before anybody acted on it. */
    strainedCycles: (record.welfare?.strainedCycles ?? 0) - (welfare?.strainedCycles ?? 0),
    /**
     * Household-cycles a family spent in the dark this season.
     *
     * Distinct from `darkBuildingCycles`, which counts *buildings* and includes
     * the stalled construction sites and the unoccupied shops. This is the one
     * §10 calls brownout-hours: somebody was home.
     */
    darkHouseholdCycles:
      (record.welfare?.darkHouseholdCycles ?? 0) - (welfare?.darkHouseholdCycles ?? 0),
    /** The same, for taps. Against `dryBuildingCycles` for the same reason. */
    dryHouseholdCycles:
      (record.welfare?.dryHouseholdCycles ?? 0) - (welfare?.dryHouseholdCycles ?? 0),
    /** Cycles this season a vacant home was on offer and nobody would take it. */
    arrivalsRefused:
      (record.welfare?.arrivalsRefused ?? 0) - (welfare?.arrivalsRefused ?? 0),
    /**
     * Dollars the district spent this season on works that pay nothing.
     *
     * The one figure in this function that measures a decision rather than an
     * outcome, and the one a reader would compare against `taxCollected` to see what
     * fraction of a season's revenue a mayor chose to get nothing for.
     */
    worksSpending:
      (record.treasury.worksSpending ?? 0) - (before?.worksSpending ?? 0),
    /** Cells deeded wild this season. */
    covenantsRegistered:
      (record.mercy?.covenantsRegistered ?? 0) - (mercy?.covenantsRegistered ?? 0),
    /** Bushes planted this season. Positive is the only direction. */
    hedgerowsPlanted:
      (record.mercy?.hedgerowsPlanted ?? 0) - (mercy?.hedgerowsPlanted ?? 0),
    /** Bushes that came in this season — planted a third of a season earlier. */
    hedgerowsGrown: (record.mercy?.hedgerowsGrown ?? 0) - (mercy?.hedgerowsGrown ?? 0),
    /** Saplings paved over before they came in. Nurture, undone. */
    hedgerowsLost: (record.mercy?.hedgerowsLost ?? 0) - (mercy?.hedgerowsLost ?? 0),
    /** Ways under a road built this season. */
    crossingsBuilt: (record.mercy?.crossingsBuilt ?? 0) - (mercy?.crossingsBuilt ?? 0),
    /**
     * Cells of the district's works a federal slab took this season.
     *
     * Against `cellsSealed` in the same line, this is how much of a seizure landed
     * on ground somebody had already paid to leave alone.
     */
    condemnedCells: (record.mercy?.condemnedCells ?? 0) - (mercy?.condemnedCells ?? 0),
    /**
     * Dollars the district paid this season for the power and water it got.
     *
     * Against `taxCollected` in the same line, the share of a season's revenue
     * that went on being lit and watered. A district in a long brownout pays less
     * of it, which is the one place a shortage is worth money to the district it
     * happens to — and it does not compensate, because the households that sat in
     * the dark are in `darkHouseholdCycles` two lines up.
     */
    utilityBilled:
      (record.treasury.utilityBilled ?? 0) - (before?.utilityBilled ?? 0),
    /** Dollars remitted upward this season. §8's extraction dial, as money. */
    tithePaid: (record.fiscal?.tithePaid ?? 0) - (fiscal?.tithePaid ?? 0),
    /** Dollars that came down this season. The other half of the same dial. */
    grantsReceived:
      (record.fiscal?.grantsReceived ?? 0) - (fiscal?.grantsReceived ?? 0),
    /**
     * Road maintenance the district wanted this season and could not fund.
     *
     * The measured austerity, and the arrow §8 draws from a governor's dial to a
     * mayor's potholes: *"cut infrastructure (roads decay, commerce slows — the
     * existing death-spiral)"*. Against `infraSpending` in the same line it is the
     * fraction of the road budget that did not happen; against
     * `treasury.roadQuality` it is why.
     */
    infraUnpaid: (record.fiscal?.infraUnpaid ?? 0) - (fiscal?.infraUnpaid ?? 0),
    /** Cycles of this season in which the road budget was short. At most `cycles`. */
    constrainedCycles:
      (record.fiscal?.constrainedCycles ?? 0) - (fiscal?.constrainedCycles ?? 0),
    /** Interchanges the mayor opened this season. */
    interchangesBuilt:
      (record.freight?.interchanges ?? 0) - (freight?.interchanges ?? 0),
    /** Trucks that entered the district this season on somebody else's business. */
    legsDispatched:
      (record.freight?.legsDispatched ?? 0) - (freight?.legsDispatched ?? 0),
    /**
     * Trucks that crossed it this season.
     *
     * Against `legsDispatched` in the same line, §5's measured freight capacity:
     * how much of what entered got out the other side, over roads at this season's
     * quality and through this season's congestion.
     */
    legsCompleted:
      (record.freight?.legsCompleted ?? 0) - (freight?.legsCompleted ?? 0),
    /** Trucks that gave up this season, and legs that never entered at all. */
    legsAbandoned:
      (record.freight?.legsAbandoned ?? 0) - (freight?.legsAbandoned ?? 0),
    legsLapsed: (record.freight?.legsLapsed ?? 0) - (freight?.legsLapsed ?? 0),
    /** Dollars of toll this season. The corridor's half of the bargain. */
    tollsEarned: (record.freight?.tollsEarned ?? 0) - (freight?.tollsEarned ?? 0),
    /**
     * Points of road quality transit wore away this season.
     *
     * The other half, and deliberately in a different unit from the one above it.
     * Against `infraSpending` in the same line it is what a share of the
     * maintenance budget went on repairing somebody else's traffic.
     */
    roadWear: (record.freight?.roadWear ?? 0) - (freight?.roadWear ?? 0),
  };
}

/**
 * A settlement stage's work.
 *
 * Takes nothing and returns nothing, and both are deliberate. Seven of the eight
 * stages run *before* there is a record — they are what makes the numbers the
 * eighth writes down — so a stage that was handed the record would be a stage that
 * could disagree with it.
 */
export type StageWork = () => void;

/** The stages a caller may put work in: all of them except the one that reports. */
export type SettlingStage = Exclude<SettlementStage, 'chronicle'>;

/** The stages whose flows this build does not have. */
const NOTHING_TO_SETTLE: StageWork = () => {};

/**
 * The season clock and the Chronicle it writes to.
 *
 * Owned by `SurfaceSim`, ticked with it, and snapshotted inside it. The stage work
 * is injected because the order belongs here and the work does not: settling a
 * tithe means reaching a treasury, and a settler that could reach a treasury could
 * reach a colony (Law 7, this file's header).
 */
export class SeasonManager {
  private readonly chronicle: SeasonRecord[] = [];

  /**
   * The settlement pipeline, in `SETTLEMENT_STAGE_ORDER`.
   *
   * A table rather than a sequence of calls, so the order lives in exactly one
   * place — the exported constant — and adding a flow is replacing one entry
   * rather than deciding where it goes. The comments name what will occupy each
   * stage, with the design section that specifies it.
   */
  private readonly stages: Record<SettlingStage, StageWork> = {
    /**
     * §5 — next season's weather actuals and the following season's forecast.
     *
     * A district already *holds* a rainfall (`WaterManager`), and it is the
     * first quantity in the game that this stage would set. It does not set it,
     * because weather is published for everybody at once and there is nobody
     * else: a district that rolled its own rain would be a district whose
     * drought no governor could have forecast, which is the opposite of what
     * this stage is for.
     */
    physics: NOTHING_TO_SETTLE,
    /**
     * §6 — power and water allocated down the governor's priority lists, then
     * facility yields.
     *
     * The district's own halves of both flows are metered every budget cycle
     * rather than here (`PowerManager`, `WaterManager`), because a brownout is
     * something that happens ninety times a season and not once at the end of
     * it. What belongs in this stage is the half a district cannot do alone: a
     * polity generating megawatts and cubic metres and running an allocation
     * down its priority list. There is no polity.
     */
    production: NOTHING_TO_SETTLE,
    /**
     * §5 — goods and fuel over measured truck capacity; interdiction.
     *
     * There are trucks now (ADR-022) and this stage is still empty, for the reason
     * `production` above is: a crossing is something that happens several times a
     * budget cycle on a physical map, not once at the end of ninety of them. The
     * legs are dispatched, driven, tolled and worn on the district's own clock, and
     * a season *reports* them (`SeasonFreight`).
     *
     * What belongs in this stage is the half a district cannot do alone: goods and
     * fuel moving between districts against a polity's measured capacity, and the
     * interdiction that stops them. Both need somewhere else for the freight to
     * come from.
     */
    freight: NOTHING_TO_SETTLE,
    /** §7 — contracts execute from measurements; escrows pay out or forfeit. */
    settlement: NOTHING_TO_SETTLE,
    /**
     * §8 — the *federal* ledger: tithes, leases, grants, bond coupons.
     *
     * Not the district's budget cycle, which is continuous and has settled
     * eighty-nine times already by the time this stage is reached.
     *
     * The tithe is here (ADR-021), injected by `SurfaceSim`, which makes this the
     * first stage in the game with work in it besides the report. The leases are
     * not: rent is paid pro rata every budget cycle, because a facility's ground is
     * sealed on every one of them. Grants are the same line as the tithe with the
     * sign reversed (§9). Bond coupons need a polity to have issued bonds.
     */
    fiscal: NOTHING_TO_SETTLE,
    /** §10 — families evaluate quality of life and leave, with a measured cause. */
    migration: NOTHING_TO_SETTLE,
    /**
     * §7 — facility and infrastructure build progress.
     *
     * The second stage in this pipeline with work in it (ADR-029), injected by
     * `SurfaceSim` on the tithe's terms: the order belongs here and reaching a
     * building site does not.
     *
     * What advances is §7's own rule — *"goods convoys arrive each season or
     * progress stalls"* — and it is a rule rather than a countdown, which is why
     * this stage was empty for fourteen ADRs. A build timer with nothing being
     * delivered into it is an authored delta wearing a clock's clothes; a build
     * timer that only moves when 120 lorries got across somebody's roads is a
     * measurement of those roads.
     */
    construction: NOTHING_TO_SETTLE,
  };

  /**
   * Put work in a stage.
   *
   * Called once, at construction, by whatever owns both this and the managers a
   * stage has to touch. There is no way to remove work and no way to add a second
   * handler to one stage: a stage is one step of one settlement, and a list of
   * things that happen at `fiscal` would be an order nobody had decided.
   */
  settles(stage: SettlingStage, work: StageWork): void {
    this.stages[stage] = work;
  }

  /** Seasons closed since the world began. */
  get settled(): number {
    return this.chronicle.length;
  }

  /** The Chronicle. Append-only: there is no method here that removes a line. */
  records(): readonly SeasonRecord[] {
    return this.chronicle;
  }

  /** The most recent season closed, or `null` in a district's first season. */
  latest(): SeasonRecord | null {
    return this.chronicle.length > 0 ? this.chronicle[this.chronicle.length - 1] : null;
  }

  /**
   * Close a season if this tick's budget cycle completed one.
   *
   * Returns the record written, or `null` on the overwhelming majority of ticks
   * where nothing is due. The caller decides what to do with it; nothing in the
   * simulation reads it back.
   *
   * `read` is a thunk rather than a value because taking a readout means
   * counting cells and walking families, and this is called on every tick of
   * every district. The cycle count is the cheap question; the district is only
   * read on the one tick in 5,400 where the answer is yes.
   *
   * It is also a thunk because of *when* it is called: the seven settling stages
   * run first, and the readout is taken after them, so a season that remitted a
   * tithe reports the pool it was left with rather than the pool it had a moment
   * earlier. §4 puts `chronicle` last for exactly this reason — a record written
   * before the settlement it describes would be a record of the season before.
   *
   * At most one season closes per call. The budget cycle fires at most once per
   * tick (`advanceTimer(..., maxFires: 1)` in `EconomyManager`), so the cycle
   * count cannot step over a boundary — and if some future cadence made it
   * possible, the next tick would close the next season rather than two records
   * claiming the same instant.
   */
  settleIfDue(cycles: number, read: () => SeasonReadout): SeasonRecord | null {
    const due = Math.floor(cycles / CYCLES_PER_SEASON);
    if (due <= this.chronicle.length) return null;

    let record: SeasonRecord | null = null;
    // Driven off the exported order rather than by writing the eight calls out,
    // so `SETTLEMENT_STAGE_ORDER` remains the single statement of §4's sequence
    // and `chronicle` cannot quietly stop being last.
    for (const stage of SETTLEMENT_STAGE_ORDER) {
      if (stage === 'chronicle') {
        record = writeRecord(this.chronicle.length + 1, cycles, read());
        this.chronicle.push(record);
      } else {
        this.stages[stage]();
      }
    }
    return record;
  }

  save(): SeasonSnapshot {
    return { chronicle: this.chronicle.map(cloneRecord) };
  }

  load(state: SeasonSnapshot): void {
    this.chronicle.length = 0;
    for (const record of state.chronicle) this.chronicle.push(cloneRecord(record));
  }

  /**
   * The Chronicle, for hashing — and nothing when it is empty.
   *
   * Two districts that disagree about what they published have diverged, so the
   * record belongs in the hash. A district that has published nothing has
   * nothing to say, and the key is omitted rather than written as `[]` — the
   * same move the command queue made in `SurfaceSim.canonicalState()`, for the
   * same reason: an empty Chronicle and no Chronicle are the same world, and
   * every golden master recorded before seasons existed is shorter than a
   * season.
   */
  canonicalState(): Canonical | undefined {
    if (this.chronicle.length === 0) return undefined;
    return this.chronicle.map((r) => r as unknown as Canonical);
  }
}

/**
 * A readout, copied rather than aliased.
 *
 * **Every optional block has to be listed here, and this is now the only place
 * that lists them.** The reason is a bug that survived two ADRs: the record-writer
 * and the record-cloner were two functions with the same list of blocks in them,
 * the cloner's list was missing `welfare`, and a district that had published a
 * quality-of-life block, been snapshotted and restored came back with the block
 * gone from its own history. Silently — a Chronicle is only read by a hash and by a
 * player — and since `canonicalState` hashes the records, that was a restored world
 * diverging from the one it was restored from. ADR-020 fixed the omission;
 * ADR-021 removed the second list, which is what made the omission possible.
 *
 * `departuresBy` is copied one level deeper because it is the readout's only nested
 * object: a line that shared a reference with the live measure would rewrite its
 * own history as the next season accumulated.
 */
function copyReadout(readout: SeasonReadout): SeasonReadout {
  const copy: SeasonReadout = {
    tick: readout.tick,
    simTimeMs: readout.simTimeMs,
    land: { ...readout.land },
    town: { ...readout.town },
    treasury: { ...readout.treasury },
    power: { ...readout.power },
    water: { ...readout.water },
  };
  // Written only when there is something to write. A key absent rather than a
  // block of zeroes, for the reason `SeasonManager.canonicalState` gives.
  if (readout.facilities) copy.facilities = { ...readout.facilities };
  if (readout.market) copy.market = { ...readout.market };
  if (readout.labour) copy.labour = { ...readout.labour };
  if (readout.welfare) {
    copy.welfare = { ...readout.welfare, departuresBy: { ...readout.welfare.departuresBy } };
  }
  if (readout.mercy) copy.mercy = { ...readout.mercy };
  if (readout.fiscal) copy.fiscal = { ...readout.fiscal };
  if (readout.freight) copy.freight = { ...readout.freight };
  return copy;
}

/** One Chronicle line, built from the readout the `chronicle` stage took. */
function writeRecord(season: number, cycles: number, readout: SeasonReadout): SeasonRecord {
  return { season, cycles, ...copyReadout(readout) };
}

/** A Chronicle line, copied rather than aliased. See `copyReadout`. */
function cloneRecord(record: SeasonRecord): SeasonRecord {
  return writeRecord(record.season, record.cycles, record);
}
